/**
 * Settings view: your details, the workstream and expense category lists, and
 * everything to do with getting data in and out.
 *
 * Nothing here talks to a network. Backups are files you save yourself.
 */

import { button, clear, confirmDialog, el, field, icon, pickFile, select, toast } from '../lib/dom.js';
import { formatDate, monthOf, today } from '../lib/dates.js';
import { LOCATIONS, normaliseDay } from '../lib/model.js';
import * as store from '../lib/store.js';
import { createAutosave } from '../lib/autosave.js';
import { downloadBackup, exportMonthCsv, parseBackup } from '../lib/export.js';
import { buildSampleDays, sampleSettings } from '../lib/sample.js';
import {
  BACKUP_DAYS_NOT_PERSISTED,
  BACKUP_DAYS_PERSISTED,
  getStoragePersistence,
  requestStoragePersistence,
} from '../lib/persist.js';

export async function renderSettings(container, params, app) {
  const settings = await store.getSettings();
  const save = createAutosave(() => store.saveSettings(settings));
  app.setPendingSave(save);

  clear(container);
  container.append(
    el('h2', { class: 'daynav__title', style: { marginBottom: '16px' } }, 'Settings'),
    renderDetails(settings, save),
    renderListEditor({
      title: 'Workstreams',
      hint: 'These appear in the picker on every line of work. Renaming one here does not change entries already saved.',
      items: settings.workstreams,
      save,
    }),
    renderListEditor({
      title: 'Expense categories',
      hint: 'Used by the expense rows on each day.',
      items: settings.expenseCategories,
      save,
    }),
    await renderBackup(app),
    renderCsv(),
    await renderDangerZone(app),
    await renderAbout(app),
  );
}

// --- Your details -----------------------------------------------------------

function renderDetails(settings, save) {
  const text = (key, label, hint, placeholder) => {
    const input = el('input', {
      type: 'text',
      class: 'input',
      value: settings[key],
      placeholder: placeholder || '',
      oninput: (event) => {
        settings[key] = event.target.value;
        save.schedule();
      },
      onblur: () => save.schedule(),
    });
    return field(label, input, hint);
  };

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h3', { class: 'card__title' }, 'Your details'),
      el('p', { class: 'card__hint' }, 'These appear on the timesheet, not on the weekly status note.'),
    ]),
    el('div', { class: 'grid-2' }, [
      text('consultantName', 'Consultant name', null, 'Your name'),
      text('companyName', 'Company name', null, 'Your Company Ltd'),
      text('companyNumber', 'Company number', null, '12345678'),
      text('clientName', 'Client name', null, 'Client Ltd'),
      text('clientContact', 'Client contact', 'Named on the signature line for approval.', 'Name or role'),
      field(
        'Default location',
        select(LOCATIONS, settings.defaultLocation, {
          onchange: (event) => {
            settings.defaultLocation = event.target.value;
            save.now();
          },
        }),
        'Used for new days.',
      ),
    ]),
  ]);
}

// --- Editable string lists --------------------------------------------------

/**
 * A reorderable list of plain strings, used for both workstreams and expense
 * categories. The array is edited in place so the settings object stays the
 * single source of truth.
 */
function renderListEditor({ title, hint, items, save }) {
  const list = el('div', { class: 'list-editor' });

  const redraw = () => {
    clear(list);
    items.forEach((value, index) => {
      list.append(
        el('div', { class: 'list-editor__row' }, [
          el('input', {
            type: 'text',
            class: 'input',
            value,
            'aria-label': `${title} item ${index + 1}`,
            oninput: (event) => {
              items[index] = event.target.value;
              save.schedule();
            },
            onblur: () => save.schedule(),
          }),
          button('↑', {
            class: 'btn btn--quiet btn--icon',
            'aria-label': 'Move up',
            disabled: index === 0,
            onclick: () => {
              [items[index - 1], items[index]] = [items[index], items[index - 1]];
              redraw();
              save.now();
            },
          }),
          button('↓', {
            class: 'btn btn--quiet btn--icon',
            'aria-label': 'Move down',
            disabled: index === items.length - 1,
            onclick: () => {
              [items[index + 1], items[index]] = [items[index], items[index + 1]];
              redraw();
              save.now();
            },
          }),
          button(icon('remove'), {
            class: 'btn btn--quiet btn--icon',
            'aria-label': `Remove ${value}`,
            // Keep at least one entry, or the pickers would have nothing to show.
            disabled: items.length <= 1,
            onclick: () => {
              items.splice(index, 1);
              redraw();
              save.now();
            },
          }),
        ]),
      );
    });
  };

  redraw();

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h3', { class: 'card__title' }, title),
      el('p', { class: 'card__hint' }, hint),
    ]),
    list,
    button('Add', {
      class: 'btn btn--small',
      onclick: () => {
        items.push('');
        redraw();
        const inputs = list.querySelectorAll('input');
        if (inputs.length) inputs[inputs.length - 1].focus();
        save.schedule();
      },
    }),
  ]);
}

// --- Backup and restore -----------------------------------------------------

async function renderBackup(app) {
  const last = await store.getMeta(store.LAST_BACKUP_KEY, null);
  const lastText = last
    ? `Last backup ${formatDate(last.slice(0, 10))}.`
    : 'No backup taken yet.';

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h3', { class: 'card__title' }, 'Backup and restore'),
      el('p', { class: 'card__hint' }, lastText),
    ]),
    el(
      'p',
      { class: 'section-note' },
      'All your data lives in this browser on this device. A backup is a single JSON file you save wherever you keep your records. Nothing is uploaded anywhere.',
    ),
    el('div', { class: 'btn-row' }, [
      button('Download backup', {
        class: 'btn btn--primary',
        onclick: async () => {
          const payload = await downloadBackup();
          toast(`Backup of ${payload.days.length} days downloaded.`);
          app.refresh();
        },
      }),
      button('Restore from backup…', {
        class: 'btn',
        onclick: () => restoreFlow(app),
      }),
    ]),
  ]);
}

/** Pick a file, show what it will do, then import on confirmation. */
async function restoreFlow(app) {
  const file = await pickFile('application/json,.json');
  if (!file) return;

  let payload;
  try {
    payload = parseBackup(file.text);
  } catch (err) {
    await confirmDialog({
      title: 'That file could not be read',
      body: el('p', err.message),
      actions: [{ id: 'ok', label: 'Close', variant: 'primary' }],
    });
    return;
  }

  const summary = await store.describeImport(payload);

  const body = el('div', [
    el(
      'p',
      `${file.name} contains ${summary.incomingDays} ${summary.incomingDays === 1 ? 'day' : 'days'}${
        summary.exportedAt ? `, backed up on ${formatDate(summary.exportedAt.slice(0, 10))}` : ''
      }.`,
    ),
    el('p', `You currently have ${summary.existingDays} ${summary.existingDays === 1 ? 'day' : 'days'} stored.`),
    el('ul', [
      el(
        'li',
        `Merge: keeps everything you have and overwrites ${summary.overlapping} ${
          summary.overlapping === 1 ? 'date that appears' : 'dates that appear'
        } in both. Your current settings are kept.`,
      ),
      el(
        'li',
        `Replace: deletes all ${summary.existingDays} stored ${summary.existingDays === 1 ? 'day' : 'days'} first, then imports the ${summary.incomingDays} from the file${summary.hasSettings ? ', including the settings saved in it' : ''}.`,
      ),
    ]),
  ]);

  const answer = await confirmDialog({
    title: 'Restore from backup',
    body,
    actions: [
      { id: 'cancel', label: 'Cancel' },
      { id: 'merge', label: 'Merge', variant: 'primary' },
      { id: 'replace', label: 'Replace everything', variant: 'danger' },
    ],
  });

  if (answer !== 'merge' && answer !== 'replace') return;

  const imported = await store.importAll(payload, answer);
  toast(`Restored ${imported} ${imported === 1 ? 'day' : 'days'}.`);
  app.refresh();
}

// --- CSV --------------------------------------------------------------------

function renderCsv() {
  const input = el('input', {
    type: 'month',
    class: 'input',
    value: monthOf(today()),
    'aria-label': 'Month to export',
  });

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h3', { class: 'card__title' }, 'CSV export'),
      el('p', { class: 'card__hint' }, 'Two files: one of days, one of expenses.'),
    ]),
    el('div', { class: 'btn-row' }, [
      input,
      button('Export month', {
        class: 'btn',
        onclick: async () => {
          const monthKey = input.value || monthOf(today());
          const days = await store.getDaysInMonth(monthKey);
          const result = exportMonthCsv(monthKey, days);
          toast(`Exported ${result.days} days and ${result.expenses} expenses.`);
        },
      }),
    ]),
  ]);
}

// --- Sample data and clearing ------------------------------------------------

async function renderDangerZone(app) {
  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [
      el('h3', { class: 'card__title' }, 'Testing and resetting'),
    ]),
    el(
      'p',
      { class: 'section-note' },
      'The app starts empty. Sample data is only for trying things out — load it into a fresh install, have a look round, then clear it before you start recording real work.',
    ),
    el('div', { class: 'btn-row' }, [
      button('Load sample data', {
        class: 'btn',
        onclick: () => loadSampleFlow(app),
      }),
      button('Clear all data…', {
        class: 'btn btn--danger',
        onclick: () => clearAllFlow(app),
      }),
    ]),
  ]);
}

async function loadSampleFlow(app) {
  const existing = await store.getAllDays();

  const answer = await confirmDialog({
    title: 'Load sample data',
    body: el('div', [
      el('p', 'This adds about five weeks of made-up days ending today, along with sample settings, so you can see how the week and month views look.'),
      existing.length
        ? el(
            'p',
            `You already have ${existing.length} ${existing.length === 1 ? 'day' : 'days'} recorded. Sample days will overwrite any of those dates that overlap.`,
          )
        : null,
    ]),
    actions: [
      { id: 'cancel', label: 'Cancel' },
      { id: 'load', label: 'Load sample data', variant: 'primary' },
    ],
  });
  if (answer !== 'load') return;

  const days = buildSampleDays(today(), 5).map((d) => normaliseDay(d, 'Bridgend'));
  await store.importAll({ app: 'timesheet', days, settings: sampleSettings() }, 'merge');
  await store.saveSettings(sampleSettings());
  toast(`Loaded ${days.length} sample days.`);
  app.refresh();
}

async function clearAllFlow(app) {
  const dayCount = (await store.getAllDays()).length;

  const confirmInput = el('input', {
    type: 'text',
    class: 'input',
    placeholder: 'DELETE',
    autocapitalize: 'characters',
    autocomplete: 'off',
    spellcheck: 'false',
    'aria-label': 'Type DELETE to confirm',
  });

  let dialogRef = null;
  confirmInput.addEventListener('input', () => {
    const confirmButton = dialogRef?.querySelector('[data-action="clear"]');
    if (confirmButton) confirmButton.disabled = confirmInput.value.trim() !== 'DELETE';
  });

  const body = el('div', [
    el(
      'p',
      `This permanently deletes all ${dayCount} ${dayCount === 1 ? 'day' : 'days'}, your settings and your lists from this device. It cannot be undone.`,
    ),
    el('p', 'If you have not taken a backup, cancel and do that first.'),
    el('label', { class: 'field__label', style: { marginTop: '12px' } }, 'Type DELETE to confirm'),
    confirmInput,
  ]);

  const answerPromise = confirmDialog({
    title: 'Clear all data',
    danger: true,
    body,
    actions: [
      { id: 'cancel', label: 'Cancel' },
      { id: 'clear', label: 'Delete everything', variant: 'danger', disabled: true },
    ],
  });

  // Grab the live dialog so the input can enable the destructive button.
  dialogRef = document.querySelector('dialog.dialog--danger');
  confirmInput.focus();

  const answer = await answerPromise;
  if (answer !== 'clear') return;

  await store.clearAllData();
  toast('All data deleted.');
  app.refresh();
}

// --- About ------------------------------------------------------------------

async function renderAbout(app) {
  const storage = await getStoragePersistence();

  // The browser decides this, not us, so explain what the answer means and
  // what follows from it.
  const explanation = storage.persisted
    ? `This browser has agreed not to clear your data to reclaim space. The backup reminder appears after ${BACKUP_DAYS_PERSISTED} days.`
    : storage.supported
      ? `The browser may clear your data if the device runs short of space or the app goes unused, so the backup reminder appears after ${BACKUP_DAYS_NOT_PERSISTED} days instead of ${BACKUP_DAYS_PERSISTED}. Installing the app to your Dock or Home Screen makes a grant much more likely.`
      : `This browser does not support persistent storage, so it may clear your data if the device runs short of space. The backup reminder appears after ${BACKUP_DAYS_NOT_PERSISTED} days instead of ${BACKUP_DAYS_PERSISTED}.`;

  return el('section', { class: 'card' }, [
    el('div', { class: 'card__header' }, [el('h3', { class: 'card__title' }, 'About')]),
    el(
      'p',
      { class: 'section-note' },
      'A personal timesheet and work log. Everything is stored in this browser using IndexedDB. The app makes no network requests and works fully offline once installed.',
    ),
    el('p', { class: 'status-line' }, [
      'Persistent: ',
      el('strong', { class: storage.persisted ? 'status-yes' : 'status-no' }, storage.persisted ? 'yes' : 'no'),
    ]),
    el('p', { class: 'section-note' }, explanation),
    el('div', { class: 'btn-row' }, [
      // A browser that refused today may well agree once the app is installed.
      storage.persisted || !storage.supported
        ? null
        : button('Ask again', {
            class: 'btn btn--small',
            title: 'Ask the browser again for persistent storage',
            onclick: async (event) => {
              event.target.disabled = true;
              const result = await requestStoragePersistence();
              toast(
                result.persisted
                  ? 'Persistent storage granted.'
                  : 'The browser still refused. Installing the app usually helps.',
              );
              app.refresh();
            },
          }),
      button('Print a blank page test', {
        class: 'btn btn--small',
        title: 'Checks your print setup produces A4 portrait',
        onclick: () => {
          const root = document.getElementById('printRoot');
          root.innerHTML =
            '<article class="print-doc"><h1>Print test</h1><p>If this page prints as A4 portrait with no app controls visible, your print setup is correct.</p></article>';
          requestAnimationFrame(() => window.print());
        },
      }),
    ]),
  ]);
}
